import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { useFormats, useTemplateInFormat, useTemplatesForFormat } from '@/hooks/useTemplates';
import { getTemplateInFormat, storedFormatIdFromPath, CUSTOM_FORMAT_PATH_ID } from '@/services/formats';
import { createCreative } from '@/services/creatives';
import type { Creative, TemplateSummary } from '@/types/api';
import { TemplatePreview } from '@/components/app/TemplatePreview';
import {
  DESIGN_CANVAS_HEIGHT_PX,
  DESIGN_CANVAS_WIDTH_PX,
} from '@/creativeComposition/elementMerge';

const FORMAT_ICONS: Record<string, string> = {
  standard: 'rectangle',
  animation: 'movie',
  carousel: 'view_carousel',
  multi: 'groups',
  interaction: 'touch_app',
  ar: 'view_in_ar',
  custom: 'palette',
};

interface NewCreativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (creative: Creative) => void;
  initialTemplateId?: string | null;
  initialFormatId?: string | null;
  campaignId?: number;
}

export function NewCreativeModal({
  isOpen,
  onClose,
  onCreated,
  initialTemplateId,
  initialFormatId,
  campaignId,
}: NewCreativeModalProps) {
  const { data: formats, loading: formatsLoading, error: formatsError } = useFormats();
  const formatList = useMemo(() => formats ?? [], [formats]);

  const [pickerStep, setPickerStep] = useState<1 | 2>(1);
  const [selectedFormatPathId, setSelectedFormatPathId] = useState<string | null>(null);
  const { data: templates, loading: templatesLoading, error: templatesError } =
    useTemplatesForFormat(selectedFormatPathId);

  const [name, setName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const templateDetail = useTemplateInFormat(selectedFormatPathId, selectedTemplateId);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const allTemplates = useMemo(() => templates ?? [], [templates]);

  const selectedTemplateSummary = useMemo(
    () => allTemplates.find((t) => t.id === selectedTemplateId) ?? null,
    [allTemplates, selectedTemplateId],
  );

  const previewTemplate = templateDetail.data;
  const previewWidth = Number(previewTemplate?.render_settings?.width_px ?? DESIGN_CANVAS_WIDTH_PX);
  const previewHeight = Number(
    previewTemplate?.render_settings?.height_px ?? DESIGN_CANVAS_HEIGHT_PX,
  );
  const previewAspect =
    Number.isFinite(previewWidth) && Number.isFinite(previewHeight) && previewHeight > 0
      ? `${previewWidth} / ${previewHeight}`
      : `${DESIGN_CANVAS_WIDTH_PX} / ${DESIGN_CANVAS_HEIGHT_PX}`;

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setSubmitError('');
    if (initialTemplateId && initialFormatId) {
      setPickerStep(2);
      setSelectedFormatPathId(initialFormatId);
      setSelectedTemplateId(initialTemplateId);
      return;
    }
    setPickerStep(1);
    setSelectedFormatPathId(initialFormatId ?? null);
    setSelectedTemplateId(initialTemplateId ?? null);
  }, [isOpen, initialTemplateId, initialFormatId]);

  useEffect(() => {
    if (!isOpen || pickerStep !== 1) return;
    if (selectedFormatPathId != null) return;
    if (formatList.length === 0) return;
    const fallback =
      initialFormatId && formatList.some((f) => f.id === initialFormatId)
        ? initialFormatId
        : formatList[0].id;
    setSelectedFormatPathId(fallback);
  }, [formatList, initialFormatId, isOpen, pickerStep, selectedFormatPathId]);

  useEffect(() => {
    if (!isOpen || pickerStep !== 2) return;
    if (selectedTemplateId) return;
    if (allTemplates.length === 0) return;
    const fallback =
      initialTemplateId && allTemplates.some((t) => t.id === initialTemplateId)
        ? initialTemplateId
        : allTemplates[0].id;
    setSelectedTemplateId(fallback);
  }, [allTemplates, initialTemplateId, isOpen, pickerStep, selectedTemplateId]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  async function handleCreate() {
    if (!name.trim() || !selectedTemplateId || !selectedFormatPathId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const full =
        previewTemplate?.id === selectedTemplateId
          ? previewTemplate
          : await getTemplateInFormat(selectedFormatPathId, selectedTemplateId);
      const created = await createCreative({
        name: name.trim(),
        format_id: storedFormatIdFromPath(selectedFormatPathId),
        template_id: selectedTemplateId,
        elements: full?.elements ?? [],
        assigned_campaign_ids: campaignId != null ? [campaignId] : [],
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create creative');
    } finally {
      setSubmitting(false);
    }
  }

  const error = formatsError || (pickerStep === 2 ? templatesError : null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Creative"
      subtitle={
        pickerStep === 1
          ? 'Step 1: choose a format (built-in or your organization custom bucket).'
          : 'Step 2: name your creative and pick a template.'
      }
      width="max-w-6xl"
      footer={
        pickerStep === 1 ? (
          <Button
            onClick={() => setPickerStep(2)}
            disabled={submitting || formatsLoading || !selectedFormatPathId}
            className="w-full py-4 text-base shadow-lg shadow-primary/20 justify-center"
          >
            Next: Choose template
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={
              submitting || templatesLoading || !name.trim() || !selectedTemplateId || !selectedFormatPathId
            }
            className="w-full py-4 text-base shadow-lg shadow-primary/20 justify-center"
          >
            {submitting ? 'Creating...' : 'Create Creative'}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-6">
        {submitError && (
          <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
            {submitError}
          </div>
        )}
        {error && (
          <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
            {error}
          </div>
        )}

        {pickerStep === 1 && (
          <div className="flex flex-col gap-4">
            <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">
              Available formats
            </p>
            {formatsLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-24 bg-surface-container-lowest rounded-xl border border-outline-variant/10 animate-pulse"
                  />
                ))}
              </div>
            )}
            {!formatsLoading && formatList.length === 0 && (
              <p className="text-sm text-on-surface-variant">No formats available.</p>
            )}
            {!formatsLoading && formatList.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {formatList.map((f) => {
                  const isSelected = selectedFormatPathId === f.id;
                  const icon = FORMAT_ICONS[f.id] ?? 'view_quilt';
                  const isCustom = f.id === CUSTOM_FORMAT_PATH_ID;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setSelectedFormatPathId(f.id)}
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'bg-primary text-on-primary shadow-md shadow-primary/20 ring-2 ring-primary/30'
                          : 'bg-surface-container-lowest text-on-surface hover:bg-primary-container'
                      }`}
                    >
                      <span className="material-symbols-outlined text-2xl">{icon}</span>
                      <span className="text-sm font-bold leading-tight">{f.name}</span>
                      {isCustom && (
                        <span className="text-[9px] uppercase font-black tracking-widest opacity-80">
                          Custom
                        </span>
                      )}
                      <span className="text-[11px] opacity-80 line-clamp-2">{f.description}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {pickerStep === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="col-span-1 lg:col-span-5 flex flex-col gap-6">
              <button
                type="button"
                className="text-xs font-bold text-primary uppercase tracking-widest self-start hover:underline"
                onClick={() => setPickerStep(1)}
                disabled={submitting}
              >
                ← Change format
              </button>

              <div className="flex flex-col gap-2">
                <label
                  className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant block"
                  htmlFor="creative-name"
                >
                  Creative Name
                </label>
                <input
                  id="creative-name"
                  className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-on-surface-variant/40 placeholder:font-normal"
                  placeholder="Summer Brand Campaign"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-bold font-headline text-on-surface">Templates</h2>
                {templatesLoading && (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-20 bg-surface-container-lowest rounded-xl border border-outline-variant/10 animate-pulse"
                      />
                    ))}
                  </div>
                )}
                {!templatesLoading && allTemplates.length === 0 && (
                  <p className="text-sm text-on-surface-variant">No templates in this format.</p>
                )}
                {!templatesLoading && allTemplates.length > 0 && (
                  <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                    {allTemplates.map((t: TemplateSummary) => {
                      const isSelected = selectedTemplateId === t.id;
                      const isCustomFormat = t.format_id.startsWith('custom-');
                      return (
                        <div
                          key={t.id}
                          onClick={() => setSelectedTemplateId(t.id)}
                          className={`p-4 rounded-xl flex items-start gap-4 cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-primary-container border-l-4 border-primary'
                              : 'bg-surface-container-lowest hover:bg-primary-container border-l-4 border-transparent hover:border-primary-container/30'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary' : 'bg-surface-container'}`}
                          >
                            <span
                              className={`material-symbols-outlined text-xl ${isSelected ? 'text-on-primary' : 'text-on-surface-variant'}`}
                            >
                              tv
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3
                                className={`font-bold text-sm truncate ${isSelected ? 'text-on-primary-container' : 'text-on-surface'}`}
                              >
                                {t.name}
                              </h3>
                              {isCustomFormat && (
                                <span className="text-[9px] uppercase font-black tracking-widest text-primary shrink-0">
                                  Custom
                                </span>
                              )}
                            </div>
                            {t.description ? (
                              <p
                                className={`text-xs mt-1 line-clamp-2 ${isSelected ? 'text-on-primary-container/80' : 'text-on-surface-variant'}`}
                              >
                                {t.description}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-1 lg:col-span-7 flex flex-col gap-4">
              <div className="bg-surface-container-lowest rounded-2xl p-6 shadow-sm flex flex-col relative overflow-hidden h-full min-h-[400px]">
                {selectedTemplateSummary ? (
                  <>
                    <div className="flex justify-between items-center mb-6 shrink-0">
                      <div className="flex flex-col">
                        <h2 className="text-xl font-extrabold font-headline text-on-surface">
                          {selectedTemplateSummary.name}
                        </h2>
                        <span className="text-xs text-on-surface-variant font-medium mt-1">
                          {selectedTemplateSummary.description}
                        </span>
                      </div>
                    </div>

                    <div className="relative overflow-hidden flex items-center justify-center bg-surface rounded-xl p-4 flex-grow min-h-[300px]">
                      <div className="relative w-full max-w-2xl mx-auto flex flex-col items-center">
                        <div
                          className="relative w-full bg-[#0b0f14] rounded-[1rem] p-[1.5%] shadow-2xl border border-black/60 overflow-hidden"
                          style={{ aspectRatio: previewAspect }}
                        >
                          {previewTemplate ? (
                            <TemplatePreview template={previewTemplate} />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary-container to-surface-container rounded-sm overflow-hidden flex items-center justify-center">
                              <div className="text-center space-y-3 opacity-40">
                                <span className="material-symbols-outlined text-7xl text-primary">
                                  tv
                                </span>
                                <p className="text-sm font-bold text-on-surface-variant">
                                  {selectedTemplateSummary.name}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-24 md:w-32 h-2 bg-[#0f141a] rounded-b-lg" />
                          <div className="w-36 md:w-48 h-1.5 bg-[#11161d] rounded-full -mt-0.5 shadow-lg" />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-grow flex flex-col items-center justify-center text-center p-8 opacity-60">
                    <span className="material-symbols-outlined text-6xl text-on-surface-variant mb-4">
                      view_in_ar
                    </span>
                    <h3 className="text-lg font-bold text-on-surface font-headline mb-2">
                      Select a Template
                    </h3>
                    <p className="text-sm text-on-surface-variant max-w-sm">
                      Choose a template from the list on the left to preview.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
